import { Fragment } from 'react';
import { Link, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { DurationType, ITicketingGameTour, ITicketingGuidedTour, ITicketingToursByYourself } from '@/components/ticketingNew/types';
import { initialCurrency } from '@/components/ticketingNew/static';
import { useRecoilValue, translationDataState, tourConfigsState } from '../../../store';
import { capitalize } from '@/utils/string';
import cn from 'classnames';
import MoreInfoBtn from '@/components/ticketingNew/components/MoreInfoBtn';

const MainWrap = styled.div<{ rtl?: boolean }>`
  display: flex;
  margin-bottom: 15px;
  min-height: 134px;

  .bl-tm-first {
    background: #ffffff;
    border-radius: 0 10px 10px 0;
    width: 108px;
    min-width: 108px;
    padding: 5px;
    text-align: center;
    ${({ rtl }) => `${rtl ? 'border-radius: 10px 0 0 10px;margin-left: 3px;' : ''}`};

    .bl-tm-first-title {
      margin-top: 6px;
      margin-bottom: 9px;
      font-weight: 500;
      font-size: 12px;
      line-height: 18px;
    }

    .bl-tm-first-price {
      font-weight: 500;
      font-size: 30px;
      line-height: 45px;
      margin-bottom: 10px;
    }

    .bl-tm-first-start-btn {
      background: #ea475c;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
      border-radius: 6px;
      color: white;
      width: 89px;
      height: 28px;
      font-weight: 500;
      font-size: 12px;
      line-height: 18px;

      i {
        margin-left: 7px;
      }

      &:active {
        opacity: 0.9;
      }
    }

    .tim-radio-pick-wrap {
      display: flex;
      height: 23px;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #a5a5a5;
      font-weight: 600;
      font-size: 15px;

      &.picked {
        color: #ea475c;
      }

      input {
        width: 17px;
        height: 17px;
        margin-left: 5px;
      }
    }
  }

  .bl-tm-second {
    overflow: hidden;
    width: 100%;
    margin-left: 1px;
    padding: 5px;
    background: #ffffff;
    border-radius: 10px 0 0 10px;
    position: relative;
    ${({ rtl }) => `${rtl ? 'border-radius: 0 10px 10px 0;padding-left: 13px;' : ''}`};

    .bl-tm-second-desc {
      min-height: 36px;
      padding: 8px 10px 0 10px;
      font-weight: 300;
      font-size: 12px;
      line-height: 18px;
      max-height: 65px;

      text-align: left;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .bl-tm-second-info-title {
      font-weight: 300;
      font-size: 12px;
      line-height: 18px;
      text-decoration-line: underline;
      color: #969595;
      margin-left: 10px;
      margin-top: 7px;
    }

    .bl-tm-second-info-wrap {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: left;
      margin-top: 7px;
      padding: 0 2px;

      .bl-tm-second-info-item {
        font-weight: 400;
        font-size: 12px;
        line-height: 18px;
        color: #333333;
        display: flex;
        align-items: center;

        img {
          width: 18px;
          height: 18px;
          margin-right: 3px;
        }
      }

      hr {
        margin: 0 10px;
        height: 13px;
        border: none;
        border-left: 1px solid black;

        &:last-child {
          display: none;
        }
      }
    }

    .bl-ticket-ending {
      position: absolute;
      height: 134px;
      right: 0;
      top: 0;
      ${({ rtl }) => `${rtl ? 'left: 0;right: initial;transform: scale(-1, 1);' : ''}`};

      .bl-ticket-end-rect {
        width: 12px;
        height: 16px;
        background: #e5e5e5;
        border-radius: 12px 0 0 12px;

        &.bl-rect-1 {
          margin-top: 27px;
        }

        &.bl-rect-2 {
          margin-top: 15px;
        }

        &.bl-rect-3 {
          margin-top: 15px;
        }
      }
    }
  }
`;

interface ITicketItem {
  ticket: ITicketingToursByYourself | ITicketingGuidedTour | (ITicketingGameTour & any);
  currencyId: number;
}

const TicketItemMobile = (props: ITicketItem & any) => {
  const { ticket, currencyId, match, onPick, pickedId } = props;
  const { durationType, limitDevices, duration, maxParticipants, tourLanguage, ticketInfoUlr } = ticket;
  const {
    available_for = 'Available for',
    start = 'start',
    devices = 'devices',

    day = 'day',
    days = 'days',
    hour = 'hour',
    hours = 'hours',
    min = 'min',
    mins = 'mins',
    from_purchase = 'from purchase',
    from_login = 'from login',
    max_participants = 'Max. participants',
    tour_language = 'Tour language',
    picked = 'Picked',
    pick = 'Pick',
  } = useRecoilValue(translationDataState);
  const { rtl } = useRecoilValue(tourConfigsState);
  const tourType = match.params.tourType;
  const durationLabels = [
    [[day], [days]],
    [[hour], [hours]],
    [[min], [mins]],
  ];
  const formatTicketTime = (duration: number) => {
    let durtionMinLeft = duration;
    const durationDays = parseInt(String(durtionMinLeft / (24 * 60)), 10);

    durtionMinLeft -= durationDays * 60 * 24;
    const durationHours = parseInt(String(durtionMinLeft / 60), 10);
    durtionMinLeft -= durationHours * 60;

    return [durationDays, durationHours, durtionMinLeft];
  };
  const currencySymbol = initialCurrency.find((cur) => cur.id === currencyId);

  const infoItems = [];
  if (durationType) {
    infoItems.push(
      <div className="bl-tm-second-info-item" key="durationType_icon">
        <img src="/ticketing/clock.svg" alt="clock-icon" />
        {formatTicketTime(duration).map((durationPart: number, num: number) => {
          if (durationPart) {
            return (
              <Fragment key={num}>
                {durationPart}
                <span>{durationPart === 1 ? durationLabels[num][0] : durationLabels[num][1]}</span>
                &nbsp;
              </Fragment>
            );
          }
          return null;
        })}{' '}
        {durationType === DurationType.from_purchase ? from_purchase : from_login}
      </div>,
    );
    infoItems.push(<hr key="durationType_hr" />);
  }
  if (limitDevices) {
    infoItems.push(
      <div className="bl-tm-second-info-item" key="limitDevices_icon">
        <img src="/ticketing/desktopMobile.svg" alt="desktop-mobile-icon" /> {limitDevices} {devices}
      </div>,
    );
    infoItems.push(<hr key="limitDevices_hr" />);
  }
  if (maxParticipants) {
    infoItems.push(
      <div className="bl-tm-second-info-item" key="maxParticipants_icon">
        <i className="fas fa-user-friends" />
        {max_participants} {maxParticipants}
      </div>,
    );
    infoItems.push(<hr key="maxParticipants_hr" />);
  }
  if (tourLanguage) {
    infoItems.push(
      <div className="bl-tm-second-info-item" key="tourLanguage_icon">
        <i className="fal fa-globe" />
        {tour_language} {tourLanguage}
      </div>,
    );
    infoItems.push(<hr key="tourLanguage_hr" />);
  }

  return (
    <MainWrap rtl={rtl}>
      <div className="bl-tm-first">
        <div className="bl-tm-first-title">{ticket.name}</div>
        <div className="bl-tm-first-price">
          {ticket.ticketPrice}
          {currencySymbol.symbol}
        </div>
        {!onPick ? (
          <Link to={`${tourType}/${ticket.id}`}>
            <button className="bl-tm-first-start-btn">
              {capitalize(start)} <i className="fas fa-chevron-right" />
            </button>
          </Link>
        ) : (
          <label className={cn('tim-radio-pick-wrap', ticket.id === pickedId && 'picked')}>
            {ticket.id === pickedId ? picked : pick} <input type="radio" onChange={() => onPick(ticket.id)} checked={ticket.id === pickedId} />
          </label>
        )}
      </div>
      <div className="bl-tm-second">
        <div className="bl-tm-second-desc" dangerouslySetInnerHTML={{ __html: ticket.description }} />
        <div className="bl-tm-second-info-title">{available_for}:</div>
        <div className="bl-tm-second-info-wrap">{infoItems}</div>
        <div className="bl-ticket-ending">
          <div className="bl-ticket-end-rect bl-rect-1" />
          <div className="bl-ticket-end-rect bl-rect-2" />
          <div className="bl-ticket-end-rect bl-rect-3" />
        </div>
        <MoreInfoBtn ticketInfoUlr={ticketInfoUlr} />
      </div>
    </MainWrap>
  );
};

export default withRouter(TicketItemMobile);
